import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { VaultKeTheme } from '../utils/theme';

const CTASection: React.FC = () => {
  const styles: { [key: string]: React.CSSProperties } = {
    cta: {
      padding: `${VaultKeTheme.spacing.xxl} ${VaultKeTheme.spacing.lg}`,
      background: `linear-gradient(135deg, ${VaultKeTheme.colors.primary} 0%, ${VaultKeTheme.colors.primaryLight} 100%)`,
      textAlign: 'center',
      color: VaultKeTheme.colors.background,
    },
    container: {
      maxWidth: '800px',
      margin: '0 auto',
    },
    title: {
      ...VaultKeTheme.typography.h2,
      color: VaultKeTheme.colors.background,
      marginBottom: VaultKeTheme.spacing.lg,
      fontSize: 'clamp(2rem, 4vw, 3rem)',
    },
    subtitle: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.background,
      marginBottom: VaultKeTheme.spacing.xl,
      opacity: 0.9,
      fontSize: 'clamp(1rem, 2vw, 1.25rem)',
    },
    button: {
      ...VaultKeTheme.components.button.primary,
      backgroundColor: VaultKeTheme.colors.background,
      color: VaultKeTheme.colors.primary,
      fontSize: '1.2rem',
      padding: '16px 32px',
      border: 'none',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    partners: {
      marginTop: VaultKeTheme.spacing.xxl,
      paddingTop: VaultKeTheme.spacing.xl,
      borderTop: `1px solid rgba(255, 255, 255, 0.2)`,
    },
    partnersTitle: {
      ...VaultKeTheme.typography.h5,
      color: VaultKeTheme.colors.background,
      marginBottom: VaultKeTheme.spacing.lg,
      opacity: 0.8,
    },
    partnersGrid: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: VaultKeTheme.spacing.xl,
      flexWrap: 'wrap',
    },
    partner: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      color: VaultKeTheme.colors.background,
      opacity: 0.7,
    },
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
      },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.1,
      boxShadow: '0 15px 35px rgba(0, 212, 170, 0.4)',
      transition: { duration: 0.3 },
    },
    tap: { scale: 0.95 },
  };

  const partnerVariants = {
    hover: {
      scale: 1.1,
      color: VaultKeTheme.colors.primary,
      transition: { duration: 0.2 },
    },
  };

  return (
    <motion.section
      style={styles.cta}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={containerVariants}
    >
      <div style={styles.container}>
        <motion.h2 style={styles.title} variants={itemVariants}>
          Ready to Transform Your Chama?
        </motion.h2>
        <motion.p style={styles.subtitle} variants={itemVariants}>
          Join thousands of community savings groups already using VaultKe to
          streamline their financial management and grow their collective wealth.
        </motion.p>
        <motion.div
          variants={itemVariants}
          whileHover={{
            scale: 1.1,
            boxShadow: '0 15px 35px rgba(0, 212, 170, 0.4)',
          }}
          whileTap={{ scale: 0.95 }}
          transition={{ type: "spring", stiffness: 400, damping: 10 }}
        >
          <Link to="/register" style={styles.button}>
            Get Started Today
          </Link>
        </motion.div>

        <motion.div style={styles.partners} variants={itemVariants}>
          <motion.h3 style={styles.partnersTitle} variants={itemVariants}>
            Trusted by Leading Financial Institutions
          </motion.h3>
          <motion.div
            style={styles.partnersGrid}
            variants={containerVariants}
          >
            <motion.div
              style={styles.partner}
              variants={partnerVariants}
              whileHover="hover"
            >
              Equity Bank
            </motion.div>
            <motion.div
              style={styles.partner}
              variants={partnerVariants}
              whileHover="hover"
            >
              KCB
            </motion.div>
            <motion.div
              style={styles.partner}
              variants={partnerVariants}
              whileHover="hover"
            >
              Co-op Bank
            </motion.div>
            <motion.div
              style={styles.partner}
              variants={partnerVariants}
              whileHover="hover"
            >
              M-Pesa
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default CTASection;